import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  EventNote as EventIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  ManageAccounts as ManageAccountsIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Psychology as PsychologyIcon,
  Quiz as QuizIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { Permission } from '../../types/auth';
import { BRAND } from '../../constants/branding';
import { FEATURE_FLAGS } from '../../constants/featureFlags';
import EapLogo from '../brand/EapLogo';

const drawerWidth = 240;
const drawerWidthCollapsed = 64;

interface MenuItem {
  text: string;
  path: string;
  icon: React.ReactElement;
  permission: Permission;
}

const menuItems: MenuItem[] = [
  
  { text: 'Sincronización ComPsych', path: '/dashboard', icon: <DashboardIcon />, permission: Permission.VIEW_DASHBOARD },
  { text: 'Atención Telefónica', path: '/phone-dashboard', icon: <PhoneIcon />, permission: Permission.VIEW_PHONE_DASHBOARD },
  { text: 'Atención WhatsApp', path: '/whatsapp', icon: <WhatsAppIcon />, permission: Permission.VIEW_WHATSAPP_INBOX },
  { text: 'Cuentas', path: '/accounts', icon: <BusinessIcon />, permission: Permission.VIEW_ACCOUNTS },
  { text: 'Clientes', path: '/clients', icon: <PeopleIcon />, permission: Permission.VIEW_CLIENTS },
  { text: 'Casos', path: '/referrals', icon: <AssignmentIcon />, permission: Permission.VIEW_REFERRALS },
  { text: 'Citas', path: '/claims', icon: <EventIcon />, permission: Permission.VIEW_CLAIMS },
];

const adminItems: MenuItem[] = [
  { text: 'Gestión de Usuarios', path: '/users', icon: <ManageAccountsIcon />, permission: Permission.MANAGE_USERS },
  { text: 'Gestión de Especialistas', path: '/specialists', icon: <PsychologyIcon />, permission: Permission.MANAGE_SPECIALISTS },
  { text: 'Plantillas de Encuesta', path: '/survey-templates', icon: <QuizIcon />, permission: Permission.MANAGE_SURVEY_TEMPLATES },
  { text: 'Configuración', path: '/settings', icon: <SettingsIcon />, permission: Permission.ACCESS_SETTINGS },
];

interface SidebarProps {
  open: boolean;
  onClose?: () => void;
  desktopOpen: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, desktopOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = useAuth();

  const handleNavigation = (path: string): void => {
    navigate(path);
    if (onClose) onClose();
  };

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.path === '/whatsapp' && !FEATURE_FLAGS.WHATSAPP_MODULE) return false;
    return hasPermission(item.permission);
  });
  const filteredAdminItems = adminItems.filter(item => hasPermission(item.permission));
  
  const currentDrawerWidth = desktopOpen ? drawerWidth : drawerWidthCollapsed;

  const drawer = (
    <>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: desktopOpen ? 'center' : 'center', 
        my: 2,
        transition: (theme) => theme.transitions.create(['align-items'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}>
        {desktopOpen ? (
          <EapLogo variant="stacked" />
        ) : (
          <EapLogo variant="mark" markSize={36} />
        )}

        {desktopOpen && user && (
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              {user.fullName}
            </Typography>
            <br />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {user.roles.join(', ')}
            </Typography>
          </Box>
        )}
      </Box>
      
      <Divider />
      
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                minHeight: 48,
                justifyContent: desktopOpen ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: location.pathname === item.path ? 'inherit' : 'text.secondary',
                  minWidth: desktopOpen ? 56 : 0,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {desktopOpen && (
                <ListItemText primary={item.text} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {filteredAdminItems.length > 0 && (
        <>
          <Divider />
          <List>
            {filteredAdminItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: desktopOpen ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: location.pathname === item.path ? 'inherit' : 'text.secondary',
                      minWidth: desktopOpen ? 56 : 0,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {desktopOpen && (
                    <ListItemText primary={item.text} />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {desktopOpen && (
        <Box sx={{ mt: 'auto', p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {BRAND.companyName} v{BRAND.version}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Entorno: desarrollo
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}
    >
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          },
        }}
      >
        {drawer}
      </Drawer>
      
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: currentDrawerWidth,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;